import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ExerciseEditor from "./components/ExerciseEditor";
import useGetExercise from "./api/useGetExercise";
import { NavLink, useParams, Link, useHistory } from "react-router-dom";
import { nullToUndefined } from "./utils/exerciseHelpers";
import { Button, TextField, Box } from "@material-ui/core";
import { ArrowBack, ArrowForward, Visibility, Delete } from "@material-ui/icons";
import { Area } from "types";
import { submitUpdateExercise } from "./utils/submitExercise";
import DeletedExerciseWarning from "./components/DeletedExerciseWarning";
import { DeleteExerciseDialog } from "./components/DeleteExerciseDialog";
import { SuccessErrorSnackbar } from "./components/SuccessErrorSnackbar";
import { useDeleteExercise } from "./api/useDeleteExercise";
import { useQueryClient } from "react-query";
import { queryKeys } from "utils/misc";

export const transformToEditor = (data: any): any => {
  // Transform the data to the format expected by the ExerciseEditor
  return {
    ...data,
    levels: data.levels.map((level: any) => ({
      ...level,
      translations: level.translations.map((trans: any) => ({
        ...trans,
        video:
          trans.video_file_url || trans.video_thumbnail_file_url
            ? {
                video_file_url: trans.video_file_url || undefined,
                video_thumbnail_file_url: trans.video_thumbnail_file_url || undefined,
              }
            : undefined,
      })),
    })),
  };
};

export const EditExercise: React.VFC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const exerciseId = parseInt(id, 10);
  const { data: exercise } = useGetExercise(exerciseId);
  const [inputId, setInputId] = useState("");
  const [showSaveWarning, setShowSaveWarning] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const queryClient = useQueryClient();
  const deleteExercise = useDeleteExercise();

  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (showSaveWarning) {
      const confirmLeave = window.confirm(t("edit_exercise.unsaved_changes_warning"));
      if (!confirmLeave) {
        e.preventDefault();
        return;
      }
    }
  };

  const handleDeleteExercise = () => {
    deleteExercise.mutateAsync(
      { id: exerciseId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getExercises());
          setDeleteConfirmationOpen(false);
          history.push(Area.CreateContent_Exercises);
        },
        onError: error => {
          console.error("Failed to delete exercise:", error);
          setDeleteConfirmationOpen(false);
          alert(t("edit_exercise.delete_error", { error: String(error) }));
        },
      }
    );
  };

  useEffect(() => {
    setShowSaveWarning(false);
  }, [id]);

  if (!id) {
    return (
      <div>
        <p>{t("edit_exercise.enter_id_prompt")}</p>
        <TextField
          label={t("edit_exercise.exercise_id_label")}
          variant="outlined"
          value={inputId}
          onChange={e => setInputId(e.target.value)}
          fullWidth
          style={{ marginBottom: 16 }}
        />

        <Button
          variant="contained"
          color="primary"
          component={NavLink}
          to={`${Area.CreateContent_EditExercise}/${inputId}`}
        >
          {t("edit_exercise.edit_button", { inputId })}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <h1>{t("edit_exercise.title")}</h1>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button
          component={Link}
          to={`${Area.CreateContent_EditExercise}/${exerciseId - 1}`}
          disabled={exerciseId <= 1}
          startIcon={<ArrowBack />}
          onClick={handleNavigation}
        >
          {t("edit_exercise.previous")}
        </Button>
        <Box>{t("edit_exercise.id_label", { exerciseId })}</Box>
        <Button
          component={Link}
          to={`${Area.CreateContent_EditExercise}/${exerciseId + 1}`}
          endIcon={<ArrowForward />}
          onClick={handleNavigation}
        >
          {t("edit_exercise.next")}
        </Button>
      </Box>
      {/* @ts-ignore */}
      {exercise?.deleted_at && <DeletedExerciseWarning />}
      {exercise && (
        <ExerciseEditor
          key={exerciseId}
          initialFormData={transformToEditor(nullToUndefined(exercise))}
          onDirty={dirty => setShowSaveWarning(dirty)}
          isSubmitting={isSubmitting}
          submitButtonText="Save Exercise"
          onSubmit={async data => {
            setIsSubmitting(true);
            try {
              const id = await submitUpdateExercise(data, nullToUndefined(exercise));
              queryClient.invalidateQueries(queryKeys.getExercises());
              setShowSaveWarning(false);
              setSnackbar({ open: true, message: t("edit_exercise.update_success", { id }), severity: "success" });
            } catch (e) {
              setSnackbar({
                open: true,
                message: t("edit_exercise.update_error", { error: String(e) }),
                severity: "error",
              });
            } finally {
              setIsSubmitting(false);
            }
          }}
          additionalActions={
            <>
              <Button
                variant="outlined"
                component={Link}
                to={`${Area.CreateContent_Exercises}/${exerciseId}`}
                startIcon={<Visibility />}
                onClick={handleNavigation}
              >
                {t("edit_exercise.view_button")}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setDeleteConfirmationOpen(true)}
                startIcon={<Delete />}
                style={{ color: "#d32f2f", borderColor: "#d32f2f" }}
              >
                {t("edit_exercise.delete_button")}
              </Button>
            </>
          }
        />
      )}

      <DeleteExerciseDialog
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        onConfirm={handleDeleteExercise}
        isLoading={deleteExercise.isLoading}
      />

      <SuccessErrorSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};
